import CheckIcon from '@mui/icons-material/Check';
import Box from '@mui/material/Box';
import { Copy as CopyIcon, ThumbsDown as ThumbsDownIcon, ThumbsUp as ThumbsUpIcon } from '@vy/ui-design-system';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { isNotNullOrUndefined } from '@vy/utils';
import { type TextUIPart, type UIMessage } from 'ai';
import { memo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { sanitizeMarkdown } from '../sanitize-markdown';
import {
  chatMessageActionIcon,
  chatMessageActionsDivider,
  chatMessageActionsRow,
  chatMessageAssistantBubble,
  chatMessageAssistantBubbleInner,
  chatMessageAssistantBubbleInnerAnimated,
  chatMessageText,
  chatMessageUserBubble,
  chatMessageUserBubbleInner,
} from '../styles';
import { RecommendedActionsPanel } from '../tool-visualizations/RecommendedActionsPanel';
import { ToolStep, getToolStatus, isToolPart } from './ChatToolSequence';
import { markdownComponents } from './MarkdownComponents';
import { ToolResultRenderer, isToolResultRenderable, type ToolInvocationPart } from './ToolResultRenderer';


type ChatMessageProps = {
  message: UIMessage;
  isNew?: boolean;
  isLoading?: boolean;
  showSuggestions?: boolean;
  onSuggestionClick?: (text: string) => void;
  onFeedback?: (messageId: string, feedback: 'Up' | 'Down') => void;
};

export const ChatMessage: React.FC<ChatMessageProps> = memo(({ message, isNew, isLoading, showSuggestions, onSuggestionClick, onFeedback }) => {
  const isUser = message.role === 'user';
  const bubbleWrapper = isUser ? chatMessageUserBubble : chatMessageAssistantBubble;
  const bubbleInner = isUser
    ? chatMessageUserBubbleInner
    : isNew
      ? chatMessageAssistantBubbleInnerAnimated
      : chatMessageAssistantBubbleInner;
  const showActions = !isUser && !isLoading;

  const hasTextContent = !isUser && message.parts.some(
    (p): p is TextUIPart => p.type === 'text' && p.text.trim().length > 0,
  );
  const showProcessingSteps = isLoading || !hasTextContent;

  const [copied, setCopied] = useState(false);
  const [feedback, setFeedback] = useState<'up' | 'down' | null>(null);

  const handleThumbUp = () => {
    const next = feedback === 'up' ? null : 'up';
    setFeedback(next);
    if (isNotNullOrUndefined(next)) {
      onFeedback?.(message.id, 'Up');
    }
  };

  const handleThumbDown = () => {
    const next = feedback === 'down' ? null : 'down';
    setFeedback(next);
    if (isNotNullOrUndefined(next)) {
      onFeedback?.(message.id, 'Down');
    }
  };

  const handleCopy = () => {
    const text = message.parts
      .filter((part): part is TextUIPart => part.type === 'text')
      .map((part) => part.text)
      .join('\n');
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Box sx={bubbleWrapper}>
      <Box sx={bubbleInner}>
        {message.parts.map((part, index) => {
          if (part.type === 'reasoning' && !isUser) {
            return null;
          }

          if (isToolPart(part) && !isUser) {
            const toolPart = part as ToolInvocationPart;
            const status = getToolStatus(toolPart);
            const hasResult = status !== 'running' && isToolResultRenderable(toolPart);
            return (
              <Box key={`part-${index}`}>
                {showProcessingSteps && <ToolStep part={toolPart} />}
                {hasResult && (
                  <>
                    {showProcessingSteps && <Box sx={{ height: '1px', bgcolor: '#e8ebf2', my: 1.5 }} />}
                    <ToolResultRenderer part={toolPart} />
                  </>
                )}
              </Box>
            );
          }

          if (part.type === 'text') {
            if (isUser) {
              return (
                <Typography key={`part-${index}`} sx={{ ...chatMessageText, color: '#fff' }}>
                  {part.text}
                </Typography>
              );
            }
            return (
              <Box key={`part-${index}`}>
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                  {sanitizeMarkdown(part.text)}
                </ReactMarkdown>
              </Box>
            );
          }

          return null;
        })}
        {showSuggestions && onSuggestionClick && (
          <RecommendedActionsPanel message={message} onActionQuery={onSuggestionClick} />
        )}
        {showActions && (
          <Box>
            <Box sx={chatMessageActionsDivider} />
            <Box sx={chatMessageActionsRow}>
              <Tooltip title={copied ? 'Copied!' : 'Copy'} placement="top">
                <IconButton
                  onClick={handleCopy}
                  size="small"
                  aria-label={copied ? 'Message copied' : 'Copy message'}
                  sx={chatMessageActionIcon}
                >
                  {copied ? <CheckIcon sx={{ fontSize: 16 }} /> : <CopyIcon width={16} height={16} />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Good response" placement="top">
                <IconButton
                  onClick={handleThumbUp}
                  size="small"
                  aria-label="Good response"
                  sx={{ ...chatMessageActionIcon, ...(feedback === 'up' && { color: '#305DFF' }) }}
                >
                  <ThumbsUpIcon width={16} height={16} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Bad response" placement="top">
                <IconButton
                  onClick={handleThumbDown}
                  size="small"
                  aria-label="Bad response"
                  sx={{ ...chatMessageActionIcon, ...(feedback === 'down' && { color: '#ef4444' }) }}
                >
                  <ThumbsDownIcon width={16} height={16} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
});
